import React, { useState } from 'react';
import { cn } from '@ais-ui/utils';
import { Button, ScrollArea, useMobile } from '@ais-ui/ui-core';
import { PiList, PiX } from 'react-icons/pi';

interface HelpLayoutProps {
  children: React.ReactNode;
  sidebar: React.ReactNode;
  header?: React.ReactNode;
}

export const HelpLayout: React.FC<HelpLayoutProps> = ({
  children,
  sidebar,
  header,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useMobile();

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-primary/5 via-background to-background">
      {/* Header */}
      {header && (
        <header className="border-b">
          <div className="flex h-14 items-center px-4">
            {/* Mobile menu button */}
            {isMobile && (
              <Button
                variant="ghost"
                size="sm"
                className="mr-2"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? (
                  <PiX className="h-4 w-4" />
                ) : (
                  <PiList className="h-4 w-4" />
                )}
              </Button>
            )}
            {header}
          </div>
        </header>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside
          className={cn(
            'w-64 border-r flex-shrink-0 transition-all duration-300 bg-background',
            // Desktop: always visible
            !isMobile && 'relative translate-x-0',
            // Mobile: overlay behavior
            isMobile && [
              'fixed inset-y-0 left-0 z-50',
              sidebarOpen ? 'translate-x-0' : '-translate-x-full',
            ]
          )}
        >
          <ScrollArea className="h-full py-4">{sidebar}</ScrollArea>
        </aside>

        {/* Mobile overlay */}
        {isMobile && sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 flex flex-col min-h-0">
          <ScrollArea className="flex-1">
            <div className="w-full py-6 px-6 pr-8">{children}</div>
          </ScrollArea>
        </main>
      </div>
    </div>
  );
};
